// src/pages/AgencyMappingsPage.tsx
//
// ★ PHASE 6, TASK 19 — Annex A story 28: "RA/PA Mapping Record Management".
//
// The catalogue half of `docs/security/SECURITY.md` S-027 and
// `docs/requirements/CLARIFICATIONS-SHORTLIST.md` Q-30: which Payment or
// Recovery Agency may act on which Healthcare Service Provider's bills. PSD
// only, both read and write.
//
// ★★ WAVE 8 (Task 5) — THIS SCREEN SAID "NOT YET ENFORCED" AND THAT STOPPED
// BEING TRUE ON 2026-08-19. S-027 is CLOSED: `checkAgencyProviderMapping`
// (`services/administration/src/agency-mapping-repository.ts`) is reached from
// `services/transfer/src/grammars/pa-payment-status.ts` through the
// `AgencyMappingPort`, so a Payment Agency's return file IS now checked
// against this catalogue before any money is recorded. The card below
// therefore says what the check actually does — authoritative PER MAPPING
// AGENCY, abstaining and SAYING SO in the audit record where an agency holds
// no rows at all — and names the three things it still does not do. The
// superseded sentence is kept here on purpose: a screen that under-claims
// misleads exactly as much as one that over-claims, and this one did.
//
// ⚠ WHAT HOLDS WHAT. `honest-boundaries.dom.test.tsx` holds the SENTENCE; it
// cannot tell you the BEHAVIOUR it describes still exists. That is held by
// `services/transfer/src/grammars/pa-payment-status.test.ts`'s six S-027 cases
// — including *"the mapping agency is the CHANNEL's counterparty and the date is
// the FILE's business date"*. If that check is ever removed, THIS SCREEN BECOMES
// A LIE and only that suite going red will say so, so the two must be read
// together.
import { CategoryBadge } from '../components/StatusBadge'
import { plainCode } from '../lib/plain-words'
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError} from '../lib/api'
import { compositeSubject, splitCompositeSubject } from '../lib/composite-subject'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type AgencyMappingSortKey = 'mappingAgencyCode' | 'mappingAgencyKind' | 'providerAgencyCode' | 'effectiveFrom'

const COLUMN_LABELS: Readonly<Record<AgencyMappingSortKey, string>> = {
  mappingAgencyCode: 'Mapping agency',
  mappingAgencyKind: 'Kind',
  providerAgencyCode: 'Provider (HSP)',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  providerAgencyCode: 'Provider (HSP)',
  note: 'Note (optional)',
} as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function AgencyMappingsPage() {
  // ★★★ WAVE 12 (P4) — the version history, for a COMPOSITE-KEY catalogue.
  //
  // ⚠ This series needs BOTH keys. Called with only the first, the route answers
  // with the agency's CURRENT BOARD instead of one pair's history — a different
  // question, and one that would render plausibly under a "Version history"
  // heading. So the picker offers a compound subject and `splitCompositeSubject`
  // reads it back; a subject that is not a pair fetches NOTHING rather than
  // guessing.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(() => {
    const pair = splitCompositeSubject(historySubject)
    if (pair === undefined) return Promise.resolve(null)
    return api.agencyMappingSeries(pair.first, pair.second, historyAsRecordedAt)
  }, [historySubject, historyAsRecordedAt])
  const { sort, onSort, interacted } = useSort<AgencyMappingSortKey>()
  const mappings = useApi(() => api.agencyMappings(sortParams(sort)), [sort.column, sort.dir])

  const [mappingAgencyCode, setMappingAgencyCode] = useState('')
  const [providerAgencyCode, setProviderAgencyCode] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = mappings.data?.mappings ?? []
  const total = mappings.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = mappings.data?.validation
  const problems = boundsProblems(
    validation,
    {
      providerAgencyCode: providerAgencyCode.trim().toUpperCase(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
  )

  const canSubmit =
    mappingAgencyCode.trim().length > 0 &&
    providerAgencyCode.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited. The consequence named is the S-027 check this catalogue gates:
   * which Payment Agency return files are accepted, and from which business date.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const agency = mappingAgencyCode.trim().toUpperCase()
    const provider = providerAgencyCode.trim().toUpperCase()
    setConfirming(
      retireInstead
        ? `Retire the mapping ${agency} -> ${provider} from ${effectiveFrom}. From that business date a payment-status return from ${agency} naming ${provider} is refused, and if no other mapping for ${agency} remains in force, the check abstains for that agency and its returns are authorised by the ingest run's grants alone. There is no undo. A mistake is corrected by another version.`
        : `Establish the mapping ${agency} -> ${provider} from ${effectiveFrom}. From that business date ${agency}'s payment-status returns are checked against this catalogue, its list is read as complete, and a provider absent from it is refused. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeAgencyMapping(mappingAgencyCode.trim().toUpperCase(), {
        providerAgencyCode: providerAgencyCode.trim().toUpperCase(),
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${mappingAgencyCode.trim().toUpperCase()} -> ${providerAgencyCode.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${mappingAgencyCode.trim().toUpperCase()} -> ${providerAgencyCode.trim().toUpperCase()} established from ${effectiveFrom}.`,
      })
      setMappingAgencyCode('')
      setProviderAgencyCode('')
      setEffectiveFrom('')
      setNote('')
      mappings.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this mapping.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">RA/PA mapping</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Which Payment or Recovery Agency may act on which Healthcare Service Provider's bills. Effective-dated, PSD-only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.agencyMappingExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/agency-mappings/export" />}
          />
        </div>
      </div>

      <div className="pt-card pt-no-print flex flex-col gap-3">
        <p className="text-sm text-[var(--sgds-body-color-subtle)]">
          <strong>Enforced for payment-status returns, one mapping agency at a time.</strong> A Payment
          Agency&rsquo;s return file is checked against this catalogue before any money is recorded against a
          bill: once this catalogue holds any row for that agency in force on the file&rsquo;s business date,
          its list is read as complete and a provider absent from it is refused. So the first row you
          establish for an agency is what turns the check on for that agency. An agency with no rows at all is
          read as not configured, the check abstains, and the return is authorised by the ingest run&rsquo;s
          own grants alone, with that abstention written into the audit record rather than passed over
          in silence, so you can see from the trail that the check was off.
        </p>
        <p className="text-sm text-[var(--sgds-body-color-subtle)]">
          ⚠ <strong>One audit note, two causes.</strong> A trail entry reading{' '}
          <em>RA/PA mapping NOT CHECKED</em> usually means what the paragraph above describes: this
          agency holds no rows. It also appears when an ingest run was assembled without this catalogue at
          all, which is a wiring fault rather than an administrative state and which no route an officer
          can reach produces. If you meet that second wording in the trail, the run was mis-composed; it
          does not mean the agency&rsquo;s rows were missing.
        </p>
        <p className="text-sm text-[var(--sgds-body-color-subtle)]">
          ⚠ <strong>Three things it still does not do.</strong> The arrangement is read as at the business
          date of the FILE, not the bill&rsquo;s date of medical service: our reading, registered as
          open question, because who settles for whom is an administrative arrangement rather than an entitlement. The
          Recovery side of this catalogue gates nothing: no recovery-return grammar exists, so a Recovery
          Agency&rsquo;s rows here are a record only. And PSD has not yet confirmed how agencies map to one
          another. The received documents give no mapping at all, so nothing recorded here is a claim about PSD&rsquo;s
          own arrangements.
        </p>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={mappings.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {mappings.error !== null ? (
        <ErrorCard title="Could not load RA/PA mappings" message={mappings.error} onRetry={mappings.reload} />
      ) : mappings.initialLoading ? (
        <LoadingCard label="Loading RA/PA mappings" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No mappings in scope"
          message="Either nothing is currently in force, or your active identity is not PSD: this catalogue is administered by PSD only. While no row is in force for an agency, the payment-status check abstains for that agency and its return files are authorised by the ingest run’s grants alone."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={mappings.loading}>
            <table className="pt-table">
              <caption className="sr-only">RA/PA mappings currently in force, one row per mapping agency and provider pair</caption>
              <thead>
                <tr>
                  <SortableTh label="Mapping agency" sortKey="mappingAgencyCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Kind" sortKey="mappingAgencyKind" sort={sort} onSort={onSort} />
                  <SortableTh label="Provider (HSP)" sortKey="providerAgencyCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.mappingAgencyCode}</td>
                    <td><CategoryBadge kind="agencyKind" value={row.mappingAgencyKind} label={plainCode(row.mappingAgencyKind)} /></td>
                    <td className="pt-mono">{row.providerAgencyCode}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force mapping{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history. Compound subject, because this
          catalogue's series is keyed by a pair; see the hooks above. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => compositeSubject(row.mappingAgencyCode, row.providerAgencyCode))}
        subjectHint="The mapping agency / provider pairs listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Mapping agency', cell: (entry) => entry.mappingAgencyCode },
            { header: 'Provider', cell: (entry) => entry.providerAgencyCode },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire a mapping</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            The mapping agency must be a Payment or Recovery Agency and the provider must be a Healthcare
            Service Provider: checked against the registered agencies before anything is written.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <SgdsInput placeholder=""
            label="Mapping agency"
            hintText="e.g. PAYCO (Payment Agency) or CPF (Recovery Agency)"
            value={mappingAgencyCode}
            required
            onSgdsInput={(e) => setMappingAgencyCode(targetValue(e))}
          />
          <BoundedField
            label={BOUNDED_FIELDS.providerAgencyCode}
            example="e.g. SGH, NUH."
            value={providerAgencyCode}
            onValueChange={setProviderAgencyCode}
            bounds={validation?.providerAgencyCode}
            problem={problems.of.providerAgencyCode}
            required
          />
          {/* A plain native date input — see WardClassesPage.tsx's own comment for why not SgdsInput/ServiceDatePicker. */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this mapping instead of establishing it
          </label>
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
